<template>
    <div class="w-full">
        <TextLoader
            class="w-full text-17 font-semibold pt-4 lg:pt-0"
            :loading="showLoaders"
            loadingClass="!w-[160px] h-8"
            v-if="showTitle"
        >
            <template v-if="call.error_message != null">{{ $help.capitalizeFirstLetter(call.error_message) }}</template>

            <template v-else>{{ call.ai?.body?.title || 'No category' }}</template>
        </TextLoader>

        <TextLoader
            class="text-14"
            :loading="showLoaders"
            loadingClass="w-full text-[1px] h-8"
            :class='{
                "mt-3": showTitle,
            }'
            v-if="showDescription"
        >
            <template v-if="call.error_message != null">The phone number provided is invalid, meaning it cannot be reached. Double-check the number for errors or missing digits, as correcting it could help you connect with a valuable contact.</template>

            <template v-else-if="call.ai == null || call.ai.body?.description == null"
                >You missed this call and the customer didn't leave a voicemail. It's important that you follow this call up as it may be
                valuable to your business.</template
            >

            <template v-else>{{ call.ai?.body?.description }}</template>
        </TextLoader>

        <TextLoader
            class="text-14 mt-3"
            :loading="showLoaders"
            loadingClass="w-full text-[1px] h-8"
            v-if="showDescription && showLoaders"
        />

        <div class="absolute top-4 right-0 flex gap-3">
            <!-- <p class="text-14">{{ $twilio.getCall(call.id)?.callStatus.status }}</p>
            <p class="text-14">{{ call.call_status }}</p>
            <p class="text-14">{{ call.transcription_status }}</p> -->

            <slot name="options" v-if="call.transcription_status != 'processing'" />

            <div v-if="showTwilioActions" class="flex gap-3 text-11 font-semibold text-white">
                <button
                    type="button"
                    class="flex items-center bg-green-600 hover:bg-green-600/70 disabled:bg-grey-500 px-4 h-8"
                    @click="acceptTwilioCall"
                    :disabled="twilioCallStatus != 'Incoming'"
                >
                    Accept
                </button>

                <button
                    type="button"
                    class="flex items-center bg-red-700 hover:bg-red-700/70 hover:cursor-pointer disabled:bg-grey-500 px-4 h-8"
                    @click="ignoreTwilioCall"
                    :disabled="
                        twilioCallStatus == 'Ended' ||
                        twilioCallStatus == 'Cancelled' ||
                        twilioCallStatus == 'Ignored' ||
                        twilioCallStatus == 'Disconnected'
                    "
                >
                    Decline
                </button>
            </div>
        </div>


        <!-- && call.recording_status == 'completed') && showCall && call.transcription_status != 'processing' -->
        <div
            v-if="call.recording_url != null"
            :class="{
                'mt-4': (showTitle || showDescription) && call.recording_url != null,
            }"
        >
            <!-- preload='metadata' @loadedmetadata="loadedmetadata" -->
            <audio v-if="call.recording_url != null" ref="audio" autobuffer>
                <source :src="call.recording_url" type="audio/mpeg" />
                Your browser does not support the audio element.
            </audio>

            <div :class="[callClass]" class="flex items-center w-full">
                <button
                    :disabled="loading"
                    class="w-8 h-8 shrink-0 relative icon-hover"
                    @click="toggle"
                    v-if="call.recording_url"
                    type="button"
                >
                    <span :class="[loading ? 'animate-ping block' : 'hidden']" class="absolute h-full w-full rounded-full bg-grey-500" />

                    <IconPause v-if="isAudioPlaying" class="h-8 w-8 relative" />
                    <IconPlayL v-else class="h-8 w-8 relative" />
                </button>

                <!-- <div v-else class="rounded-bl-[10px] rounded-tr-[10px] h-8 shrink-0" /> -->

                <p class="px-3 text-12" v-if="call.recording_url">{{ $help.fmtMSS(playTime) }}</p>
                <!-- <p class="mx-3 h-4 bg-grey-300 animate-shimmer w-[44px]" v-else>&nbsp;</p> -->

                <!-- @click="sliderClick"  -->
                <div class="w-full h-2 flex items-center cursor-pointer relative" v-if="call.recording_url">
                    <div class='absolute top-0 -left-2 -right-2 opacity-0 z-10'>
                        <input type="range" class="h-2 w-full call-slider" 
                            min="0" max="100" :step='1' 
                            v-model="percentage" 
                            @input="sliderChange"
                        />
                    </div>

                    <div class="w-full h-0.5 bg-grey-500 relative">
                        <div
                            :style="{
                                width: percentage + '%',
                            }"
                            class="absolute h-0.5 bg-black dark:bg-coal left-0 top-0"
                        />
                    </div>
                </div>

                <!-- <div v-else class="w-full h-0.5 bg-grey-300 animate-shimmer" /> -->

                <p class="pl-3 text-12 text-grey-900" v-if="call.recording_url">
                    {{ $help.fmtMSS(length) }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import TextLoader from '~/components/customer/input/text-loader.vue'
export default {
    props: {
        call: Object,
        callClass: {
            type: String,
            default: '',
        },
        showTitle: { type: Boolean, default: true },
        showDescription: { type: Boolean, default: true },
        showCall: { type: Boolean, default: true },
    },

    computed: {
        showLoaders() {
            return this.call.transcription_status == 'processing'
        },

        audio() {
            return this.$refs.audio
        },

        twilioCall() {
            if (typeof this.$twilio?.getCall !== 'function') {
                return null
            }

            return this.$twilio.getCall(this.call?.id) || null
        },

        twilioCallStatus() {
            return this.twilioCall?.callStatus?.status || null
        },

        showTwilioActions() {
            return this.twilioCall != null && ['Ended', 'Cancelled', 'Disconnected'].indexOf(this.twilioCallStatus) == -1
        }
    },

    data() {
        return {
            audioLoad: false,
            loaded: false,
            isAudioPlaying: false,
            playTime: 0,
            percentage: 0,
            length: this?.call?.recording_duration || 0,
            newTime: 0
        }
    },

    methods: {
        loadedmetadata(event) {
            this.length = parseInt(Math.ceil(this.audio.duration))
        },

        load(callback = null, force = false) {
            // console.log('load audioLoad', this.audioLoad)
            if (! this.audioLoad || force) {
                this.audio.load();
                this.audioLoad = true
                this.playTime = 0;
                this.percentage = 0;
                this.newTime = 0;
                this.length = parseInt(Math.ceil(this.call.recording_duration))

                this.$event.$on('audio-playing', (url) => {
                    if (url != this.call.recording_url) {
                        this.toggle()
                    }
                })

                // this.audio.addEventListener('loadeddata', () => {
                //     console.log('loadeddata', parseInt(Math.ceil(this.audio.duration)))
                //     this.length = parseInt(Math.ceil(this.audio.duration))
                //     this.loaded = true

                //     return callback != null ? callback() : null
                // })

                this.audio.addEventListener('timeupdate', () => {
                    this.playTime = parseInt(Math.ceil(this.audio.currentTime));
                    this.percentage = (100 / this.audio.duration) * this.audio.currentTime
                })

                this.audio.addEventListener('ended', () => {
                    this.isAudioPlaying = false
                })
            } else if (callback != null) {
                return callback();
            }
        },

        toggle() {
            this.fn(() => {
                this.audio.paused ? this.audio.play() : this.audio.pause()
                this.isAudioPlaying = this.audio.paused ? false : true

                if (this.isAudioPlaying) {
                    this.$event.$emit('audio-playing', this.call.recording_url)
                }
            })
        },

        sliderChange(event) {
            this.fn(() => {
                if (! this.isAudioPlaying) {
                 this.toggle();
            }

                let newTime = Math.floor((this.audio.duration / 100) * event.target.value)
                this.newTime = newTime
                // console.log('newTime', newTime, isFinite(newTime))
                if (! isFinite(newTime)) { return; }
                this.audio.currentTime = newTime
            })
        },

        fn(callback) {
            this.load(callback)
        },

        acceptTwilioCall() {
            if (typeof this.twilioCall?.accept === 'function') {
                this.twilioCall.accept()
            }
        },

        ignoreTwilioCall() {
            if (typeof this.twilioCall?.ignore === 'function') {
                this.twilioCall.ignore()
            }
        }
    },

    watch: {
        call() {
            this.load(null, true)
        }
        // 'call.recording_status': {
        //     handler(newVal) {
        //         if (newVal == 'completed') {
        //             setTimeout(() => {
        //                 this.load()
        //             }, 10000)
        //         }
        //     }
        // }
    },

    // data() {
    //     return {
    //         load: false,
    //         loading: false,
    //         playing: false,
    //         muted: false,
    //         duration: '0:00',
    //         current: '0:00',
    //         percentage: 0,
    //     }
    // },

    // beforeDestroy() {
    //     // console.log('call beforeDestroy')
    // },

    // mounted() {
    //     if (this.call != null) {
    //         this.setupCall()
    //     }
    // },

    // methods: {
    //     setupCall() {
    //         if (this.call.recording_duration != null) {
    //             this.duration = this.$help.fmtMSS(this.call.recording_duration)
    //         }

    //         this.$event.$on('audio-playing', (url) => {
    //             if (url == this.call.recording_url) {
    //                 return
    //             }

    //             if (this.playing) {
    //                 this.toggle()
    //             }
    //         })
    //     },

    //     start(time = 0, callback = null) {
    //         this.load = true
    //         this.loading = true

    //         this.$nextTick(() => {
    //             this.audio.currentTime = time
    //             this.audio.addEventListener('loadedmetadata', this.init)
    //             this.audio.addEventListener('timeupdate', this.playbackListener)
    //             this.audio.addEventListener('canplaythrough', this.canPlay)
    //             this.audio.addEventListener('ended', () => {
    //                 this.playing = false
    //             })

    //             this.toggle()
    //         })
    //     },

    //     jump(time) {
    //         if (!this.load) {
    //             this.start(time)
    //             return
    //         }

    //         this.audio.currentTime = time

    //         if (!this.playing) {
    //             this.toggle()
    //         }
    //     },

    //     canPlay() {
    //         this.loading = false
    //     },

    //     init() {
    //         this.duration = this.$help.fmtMSS(Math.round(this.audio.duration))
    //     },

    //     playbackListener() {
    //         this.current = this.$help.fmtMSS(Math.round(this.audio.currentTime))
    //         this.setPercentage()
    //     },

    //     toggle() {
    //         if (!this.load) {
    //             this.start()
    //             return
    //         }

    //         this.playing = !this.playing

    //         if (this.playing) {
    //             this.$event.$emit('audio-playing', this.call.recording_url)
    //             this.audio.play()
    //         } else {
    //             this.audio.pause()
    //         }
    //     },

    //     setPercentage() {
    //         this.percentage = (100 / this.audio.duration) * this.audio.currentTime
    //     },

    //     sliderClick(event) {
    //         if (!this.load) {
    //             this.start()

    //             setTimeout(() => {
    //                 this.sliderClick(event)
    //             }, 200)
    //             return
    //         }

    //         if (this.audio == undefined || this.audio.readyState <= 2) {
    //             setTimeout(() => {
    //                 this.sliderClick(event)
    //             }, 200)

    //             return
    //         }

    //         this.audio.currentTime = Math.round((parseInt(this.audio.duration) / event.target.clientWidth) * event.offsetX)

    //         if (!this.playing) {
    //             this.toggle()
    //         }
    //     },

    //     sliderChange(event) {
    //         if (!this.load || !this.playing) {
    //             return this.start(0, () => {
    //                 this.sliderChange(event)
    //             })
    //         } else {
    //             try {
    //                 this.audio.currentTime = (this.audio.duration / 100) * event.target.value
    //             } catch (e) {
    //                 if (e.name != 'TypeError') {
    //                     throw e
    //                 }
    //             }
    //         }
    //     },

    //     isAudioPlaying() {
    //         return this.audio && this.audio.currentTime > 0 && !this.audio.paused && !this.audio.ended && this.audio.readyState > 2
    //     },
    // },

    // computed: {
    //     audio() {
    //         return this.$refs.audio
    //     },

    //     showLoaders() {
    //         return this.call.transcription_status == 'processing'
    //     }
    // },

    // watch: {
    //     call() {
    //         // console.log('call updated')
    //         this.setupCall()
    //     },
    // },

    components: {
        TextLoader,
    },
}
</script>
